// ============================================================================
// screens.js — all meta-game DOM overlays: main menu, level select, pre-battle
// briefing (advancing dialogue + objectives), results/rank, pause, options, and
// the in-battle objectives panel. A GameManager wires the buttons via
// setController(); screens never touch game logic directly.
// ============================================================================

const CSS = `
#scr { position:fixed; inset:0; z-index:60; pointer-events:none; font-family:'Trebuchet MS',system-ui,sans-serif; }
#scr .screen { position:fixed; inset:0; display:none; flex-direction:column; align-items:center; justify-content:center;
  pointer-events:auto; background:radial-gradient(circle at 50% 38%, rgba(18,10,6,.86), rgba(3,2,2,.96)); }
#scr .screen.show { display:flex; }
#scr .brand { font-size:clamp(40px,9vw,110px); font-weight:900; letter-spacing:.2em; color:#d7dbe0;
  text-shadow:0 0 26px rgba(255,106,26,.5),0 4px 12px #000; }
#scr .sub { color:#ff6a1a; letter-spacing:.42em; font-size:13px; margin-top:6px; }
#scr .btn { pointer-events:auto; cursor:pointer; margin:7px; padding:13px 40px; min-width:280px; text-align:center;
  border:1px solid rgba(255,106,26,.35); background:rgba(8,6,5,.6); color:#ffb257; letter-spacing:.26em;
  font-size:14px; font-weight:700; transition:all .16s ease; }
#scr .btn:hover { background:rgba(255,106,26,.18); color:#fff; box-shadow:0 0 22px rgba(255,106,26,.35); }
#scr .btn.dim { opacity:.4; cursor:not-allowed; }
#scr .btn.small { min-width:150px; padding:10px 22px; font-size:12px; }
#scr .col { display:flex; flex-direction:column; align-items:center; }
#scr .row { display:flex; gap:10px; flex-wrap:wrap; justify-content:center; }
#scr h2 { color:#e6ddd2; font-size:clamp(24px,4vw,40px); font-weight:900; letter-spacing:.08em; text-align:center; text-shadow:0 3px 8px #000; }
#scr .act { color:#ff7a2a; letter-spacing:.3em; font-size:12px; margin-bottom:4px; }
#scr .msub { color:#b9b2a8; font-style:italic; font-size:15px; margin:6px 0 18px; text-align:center; max-width:640px; }

/* menu bg = title art */
#screen-menu { background:
  linear-gradient(to bottom, rgba(6,4,3,.55), rgba(6,4,3,.4) 40%, rgba(6,4,3,.92)),
  url('art/title.png') center 26% / cover no-repeat, #060403; }

/* level select grid */
#lvl-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(150px,1fr)); gap:10px; width:min(880px,88vw); max-height:62vh; overflow-y:auto; padding:6px; }
.lvl-card { pointer-events:auto; cursor:pointer; border:1px solid rgba(255,106,26,.25); background:rgba(10,7,6,.7); padding:12px; text-align:left; transition:all .15s; }
.lvl-card:hover { border-color:rgba(255,106,26,.7); background:rgba(255,106,26,.1); }
.lvl-card.locked { opacity:.35; cursor:not-allowed; filter:grayscale(1); }
.lvl-card .n { color:#ffb257; font-size:11px; letter-spacing:.12em; }
.lvl-card .t { color:#e6ddd2; font-size:14px; font-weight:700; margin:3px 0; line-height:1.25; }
.lvl-card .rk { float:right; font-size:20px; font-weight:900; }
.rk.S{color:#ffd07a} .rk.A{color:#9ad0ff} .rk.B{color:#b9b2a8} .rk.C{color:#8c857c}

/* briefing dialogue */
#brief-box { width:min(760px,90vw); }
#brief-dlg { min-height:120px; border:1px solid rgba(255,106,26,.3); background:rgba(6,4,3,.7); padding:18px 22px; margin-bottom:14px; cursor:pointer; display:flex; gap:18px; align-items:flex-start; }
#brief-portrait { width:104px; height:104px; object-fit:cover; border:1px solid rgba(255,106,26,.45); flex:0 0 auto; background:#0a0706; box-shadow:0 0 16px rgba(255,106,26,.15); }
#brief-dlg .dtext { flex:1; min-width:0; }
#brief-who { color:#ff7a2a; font-weight:900; letter-spacing:.18em; font-size:14px; margin-bottom:8px; }
#brief-line { color:#e8e2d8; font-size:17px; line-height:1.6; }
#brief-hint { color:#6c665e; font-size:11px; letter-spacing:.2em; margin-top:12px; text-align:right; }
#brief-obj { border-left:2px solid rgba(255,106,26,.5); padding:4px 0 4px 14px; margin:14px 0; }
#brief-obj .o { color:#e8c9a0; font-size:14px; margin:4px 0; }
#brief-obj .o::before { content:'◆ '; color:#ff7a2a; }
#brief-obj .f { color:#d98; font-size:13px; } #brief-obj .f::before{content:'✖ ';color:#c55;}

/* results */
#res-verdict { font-size:clamp(34px,7vw,74px); font-weight:900; letter-spacing:.16em; }
#res-verdict.win{color:#ffd07a;text-shadow:0 0 34px rgba(255,140,40,.6)} #res-verdict.lose{color:#b81f1f;text-shadow:0 0 30px rgba(200,20,20,.5)}
#res-story { color:#c9beb0; font-size:15px; font-style:italic; max-width:640px; text-align:center; line-height:1.7; margin:12px 0; }
#res-stats { display:flex; gap:34px; margin:14px 0 20px; }
#res-stats .s { text-align:center; } #res-stats .v { color:#ffb257; font-size:30px; font-weight:900; } #res-stats .k { color:#8c857c; font-size:11px; letter-spacing:.2em; }
#res-rank { font-size:64px; font-weight:900; margin-bottom:4px; }

/* options */
#opt-box { width:min(520px,88vw); }
#opt-box .opt { display:flex; align-items:center; justify-content:space-between; padding:12px 4px; border-bottom:1px solid rgba(255,106,26,.12); }
#opt-box .opt label { color:#d7dbe0; font-size:14px; letter-spacing:.06em; }
#opt-box input[type=range]{ width:200px; accent-color:#ff6a1a; }
#opt-box .tog { cursor:pointer; color:#ffb257; border:1px solid rgba(255,106,26,.4); padding:5px 16px; font-weight:700; letter-spacing:.1em; }

/* in-battle objectives panel */
#hud-obj { position:fixed; left:20px; top:70px; z-index:11; pointer-events:none; font-family:'Trebuchet MS',sans-serif; text-shadow:0 2px 5px #000; display:none; }
#hud-obj .hdr { color:#ff7a2a; font-size:11px; letter-spacing:.26em; font-weight:800; margin-bottom:6px; }
#hud-obj .obj { color:#e8dccb; font-size:14px; margin:3px 0; }
#hud-obj .obj .bx { color:#6c665e; margin-right:6px; }
#hud-obj .obj.done { color:#7dff8a; } #hud-obj .obj.done .bx { color:#7dff8a; }
#hud-obj .obj .pg { color:#ffb257; font-weight:700; }
#hud-obj .fail { color:#e79; font-size:12px; margin-top:4px; }

/* the battle HUD only shows while actually fighting (body.playing) */
body:not(.playing) #hp, body:not(.playing) #titan, body:not(.playing) #kills,
body:not(.playing) #combo, body:not(.playing) #morale, body:not(.playing) #boss,
body:not(.playing) #objective, body:not(.playing) #dmg-layer, body:not(.playing) #minimap,
body:not(.playing) #barks { display:none !important; }
`;

// dialogue speaker → generated portrait + display name (shared with ui/barks.js)
export const PORTRAIT = {
  BENJEN: 'art/portrait_benjen.png', HALDRIC: 'art/portrait_haldric.png',
  SERA: 'art/portrait_sera.png', GARRICK: 'art/portrait_garrick.png',
  LYSA: 'art/portrait_lysa.png', BRAM: 'art/portrait_bram.png',
  ISOLDE: 'art/portrait_isolde.png', MAGMAR: 'art/portrait_magmar.png',
};
export const SPEAKER = {
  BENJEN: 'BENJEN DAGUTH', HALDRIC: 'WARMASTER HALDRIC', SERA: 'SERA VOLK',
  GARRICK: 'OLD GARRICK', LYSA: 'LYSA FENN', BRAM: 'BRAM DAGOR',
  ISOLDE: 'MOTHER ISOLDE', CORAN: 'CORAN VAEL', MAGMAR: 'OURANG-VAEL',
};
// map a full general/warlord name → dialogue speaker key (for barks)
export const NAME2SPEAKER = {
  'Haldric Vane': 'HALDRIC', 'Sera Volk': 'SERA', 'Old Garrick': 'GARRICK',
  'Lysa Fenn': 'LYSA', 'Bram Dagor': 'BRAM', 'Mother Isolde': 'ISOLDE', 'Coran Vael': 'CORAN',
};

export class Screens {
  constructor(hudRoot) {
    const style = document.createElement('style'); style.textContent = CSS; document.head.appendChild(style);
    const root = document.createElement('div'); root.id = 'scr'; hudRoot.appendChild(root);
    root.innerHTML = `
      <div id="screen-menu" class="screen">
        <div class="brand">TITANLORD</div><div class="sub">THE ASHEN HOST</div>
        <div class="col" style="margin-top:34px">
          <div class="btn" data-a="conquest">⚔ CONQUEST — WAR FOR THE REALM</div>
          <div class="btn" data-a="new">NEW CAMPAIGN</div>
          <div class="btn" data-a="continue">CONTINUE</div>
          <div class="btn" data-a="levels">CHOOSE BATTLE</div>
          <div class="btn" data-a="options">OPTIONS</div>
        </div>
      </div>
      <div id="screen-levels" class="screen">
        <h2>CHOOSE BATTLE</h2><div class="msub">The war against the Magmar Dynasty</div>
        <div id="lvl-grid"></div>
        <div class="btn small" data-a="menu" style="margin-top:16px">◂ BACK</div>
      </div>
      <div id="screen-brief" class="screen">
        <div id="brief-box" class="col" style="align-items:stretch">
          <div class="act" id="brief-act"></div>
          <h2 id="brief-name" style="text-align:left"></h2>
          <div class="msub" id="brief-sub" style="text-align:left;margin:6px 0 14px"></div>
          <div id="brief-dlg"><img id="brief-portrait" alt=""><div class="dtext"><div id="brief-who"></div><div id="brief-line"></div><div id="brief-hint">CLICK TO CONTINUE ▸</div></div></div>
          <div id="brief-obj"></div>
          <div class="row" style="justify-content:flex-end">
            <div class="btn small" data-a="menu">◂ RETREAT</div>
            <div class="btn" data-a="begin">TAKE THE FIELD ▸</div>
          </div>
        </div>
      </div>
      <div id="screen-results" class="screen">
        <div id="res-verdict"></div>
        <div id="res-rank"></div>
        <div id="res-story"></div>
        <div id="res-stats"></div>
        <div class="row">
          <div class="btn small" data-a="menu">MENU</div>
          <div class="btn small" data-a="retry">RETRY</div>
          <div class="btn" data-a="next">NEXT BATTLE ▸</div>
        </div>
      </div>
      <div id="screen-pause" class="screen">
        <h2>PAUSED</h2>
        <div class="col" style="margin-top:20px">
          <div class="btn" data-a="resume">RESUME</div>
          <div class="btn" data-a="retry">RESTART BATTLE</div>
          <div class="btn" data-a="options">OPTIONS</div>
          <div class="btn" data-a="menu">QUIT TO MENU</div>
        </div>
      </div>
      <div id="screen-options" class="screen">
        <h2>OPTIONS</h2>
        <div id="opt-box" style="margin-top:16px">
          <div class="opt"><label>Master Volume</label><input id="opt-vol" type="range" min="0" max="100"></div>
          <div class="opt"><label>Look Sensitivity</label><input id="opt-sens" type="range" min="20" max="120"></div>
          <div class="opt"><label>Invert Look (Y)</label><div class="tog" id="opt-inv"></div></div>
        </div>
        <div class="btn small" data-a="opt-back" style="margin-top:16px">◂ BACK</div>
      </div>
      <div id="hud-obj"></div>
    `;
    this.root = root;
    this.hudObj = root.querySelector('#hud-obj');
    this._dlg = { lines: [], i: 0, onEnd: null };
    this._optBack = 'menu';
    root.querySelector('#brief-dlg').addEventListener('click', () => this._advance());
  }

  setController(gm) {
    this.gm = gm;
    this.root.querySelectorAll('[data-a]').forEach((el) => {
      el.addEventListener('click', () => this._act(el.getAttribute('a') || el.dataset.a, el));
    });
    // options live controls
    const vol = this.root.querySelector('#opt-vol'), sens = this.root.querySelector('#opt-sens'), inv = this.root.querySelector('#opt-inv');
    vol.addEventListener('input', () => gm.setOption('volume', +vol.value / 100));
    sens.addEventListener('input', () => gm.setOption('sens', +sens.value / 10000));
    inv.addEventListener('click', () => { gm.setOption('invertY', !gm.options.invertY); this._syncOptions(); });
  }

  _act(a, el) {
    if (el && el.classList.contains('dim')) return;
    const gm = this.gm;
    switch (a) {
      case 'conquest': gm.openConquest(); break;
      case 'new': gm.startCampaign(); break;
      case 'continue': gm.continueCampaign(); break;
      case 'levels': gm.openLevels(); break;
      case 'menu': gm.gotoMenu(); break;
      case 'begin': gm.beginBattle(); break;
      case 'next': gm.nextLevel(); break;
      case 'retry': gm.retryLevel(); break;
      case 'resume': gm.resume(); break;
      case 'options': this._optBack = gm.state === 'paused' ? 'pause' : 'menu'; gm.openOptions(); break;
      case 'opt-back': this.hideAll(); if (this._optBack === 'pause') gm.showPause(); else gm.gotoMenu(); break;
    }
  }

  hideAll() { this.root.querySelectorAll('.screen').forEach((s) => s.classList.remove('show')); }
  _show(id) { this.hideAll(); this.root.querySelector('#' + id).classList.add('show'); }

  showMenu(save) {
    const cont = this.root.querySelector('[data-a="continue"]');
    cont.classList.toggle('dim', !(save && save.unlocked > 0));
    this._show('screen-menu');
  }

  showLevels(campaign, save) {
    const grid = this.root.querySelector('#lvl-grid'); grid.innerHTML = '';
    campaign.forEach((lv, i) => {
      const unlocked = i <= (save.unlocked || 0);
      const rank = save.ranks && save.ranks[lv.id];
      const card = document.createElement('div');
      card.className = 'lvl-card' + (unlocked ? '' : ' locked');
      card.innerHTML = `${rank ? `<span class="rk ${rank}">${rank}</span>` : ''}<div class="n">BATTLE ${i + 1}</div><div class="t">${lv.name}</div>`;
      if (unlocked) card.addEventListener('click', () => this.gm.selectLevel(i));
      grid.appendChild(card);
    });
    this._show('screen-levels');
  }

  showBriefing(level, index) {
    this.root.querySelector('#brief-act').textContent = level.act + (level.kind ? '  ·  ' + (KIND_LABEL[level.kind] || level.kind.toUpperCase()) : '');
    this.root.querySelector('#brief-name').textContent = `BATTLE ${index + 1} — ${level.name}`;
    this.root.querySelector('#brief-sub').textContent = level.subtitle || '';
    const ob = this.root.querySelector('#brief-obj');
    ob.innerHTML = (level.objectives || []).map((o) => `<div class="o">${objLabel(o)}</div>`).join('')
      + (level.fail || []).map((f) => `<div class="o f">${failLabel(f)}</div>`).join('');
    this._dlg = { lines: level.intro || [], i: 0 };
    this._renderLine();
    this._show('screen-brief');
  }
  _renderLine() {
    const d = this._dlg;
    const box = this.root.querySelector('#brief-dlg');
    if (!d.lines.length) { box.style.display = 'none'; return; }
    box.style.display = 'flex';
    const ln = d.lines[Math.min(d.i, d.lines.length - 1)];
    this.root.querySelector('#brief-who').textContent = SPEAKER[ln.who] || ln.who;
    this.root.querySelector('#brief-line').textContent = '"' + ln.line + '"';
    const port = this.root.querySelector('#brief-portrait');
    const url = PORTRAIT[ln.who];
    if (url) { port.src = url; port.style.display = 'block'; } else port.style.display = 'none';
    this.root.querySelector('#brief-hint').style.visibility = d.i < d.lines.length - 1 ? 'visible' : 'hidden';
  }
  _advance() { const d = this._dlg; if (d.i < d.lines.length - 1) { d.i++; this._renderLine(); } }

  showResults(win, level, index, stats, hasNext) {
    const v = this.root.querySelector('#res-verdict');
    v.className = win ? 'win' : 'lose';
    v.textContent = win ? 'VICTORY' : 'DEFEAT';
    this.root.querySelector('#res-rank').innerHTML = win ? `<span class="rk ${stats.rank}">${stats.rank}</span>` : '';
    this.root.querySelector('#res-story').textContent = win ? (level.win || '') : (level.lose || stats.reason || '');
    this.root.querySelector('#res-stats').innerHTML = `
      <div class="s"><div class="v">${stats.kills}</div><div class="k">SLAIN</div></div>
      <div class="s"><div class="v">${fmtTime(stats.time)}</div><div class="k">TIME</div></div>
      <div class="s"><div class="v">${stats.combo}</div><div class="k">MAX COMBO</div></div>`;
    const next = this.root.querySelector('[data-a="next"]');
    next.classList.toggle('dim', !(win && hasNext));
    next.textContent = win && !hasNext ? 'CAMPAIGN WON' : 'NEXT BATTLE ▸';
    this._show('screen-results');
  }

  showPause() { this._show('screen-pause'); }
  showOptions(options) { this._syncOptions(); this._show('screen-options'); }
  _syncOptions() {
    const o = this.gm.options;
    this.root.querySelector('#opt-vol').value = Math.round(o.volume * 100);
    this.root.querySelector('#opt-sens').value = Math.round(o.sens * 10000);
    this.root.querySelector('#opt-inv').textContent = o.invertY ? 'ON' : 'OFF';
  }

  setObjHudVisible(v) { this.hudObj.style.display = v ? 'block' : 'none'; }
  updateObjHud(world) {
    if (!world.objectives.length) { this.hudObj.innerHTML = ''; return; }
    let html = '<div class="hdr">OBJECTIVES</div>';
    for (const t of world.objectives) {
      const prog = t.max > 1 ? ` <span class="pg">${Math.floor(t.cur)}/${t.max}</span>` : '';
      html += `<div class="obj${t.done ? ' done' : ''}"><span class="bx">${t.done ? '✔' : '▢'}</span>${t.label}${t.done ? '' : prog}</div>`;
    }
    for (const t of (world._failConds || [])) {
      if (t.type === 'protect') html += `<div class="fail">✖ Keep ${t.p.name} alive</div>`;
      if (t.type === 'timeout') html += `<div class="fail">⏳ ${Math.max(0, Math.ceil(t.max - world.levelTime))}s remaining</div>`;
      if (t.type === 'holdBastions') html += `<div class="fail">✖ Hold your bastions</div>`;
    }
    this.hudObj.innerHTML = html;
  }
}

const KIND_LABEL = {
  rout: 'OPEN BATTLE', siege: 'SIEGE', defense: 'DEFENSE', ambush: 'AMBUSH',
  duel: 'DUEL', laststand: 'LAST STAND', escort: 'ESCORT', gauntlet: 'GAUNTLET', race: 'RACE',
};

function objLabel(o) {
  switch (o.type) {
    case 'rout': return `Rout the Magmar host — slay ${o.count}`;
    case 'slayWarlords': return `Slay ${o.count} Magmar warlord${o.count > 1 ? 's' : ''}`;
    case 'slayWarlord': return `Slay ${o.name}`;
    case 'slayDynast': return 'Slay the Dynast, Ourang-Vael';
    case 'capture': return `Capture ${o.count} bastion${o.count > 1 ? 's' : ''}`;
    case 'captureAll': return 'Capture every bastion';
    case 'survive': return `Hold the line for ${o.time}s`;
    case 'reach': return o.label || 'Reach the objective';
    case 'morale': return `Turn the tide — morale ${o.value}`;
    default: return o.type;
  }
}
function failLabel(f) {
  if (f.type === 'protect') return `Keep ${f.name} alive`;
  if (f.type === 'timeout') return `Win before ${f.time}s`;
  if (f.type === 'holdBastions') return 'Do not let your bastions fall';
  return f.type;
}
function fmtTime(s) { s = Math.floor(s); return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`; }
